/**
 * Bordereau Constants
 * 
 * Centralized constants for the bordereau feature.
 * Error messages are user-friendly and aligned with backend error codes.
 */

// ═══════════════════════════════════════════════════════════════════════════
// ERROR CODES (mirrored from backend)
// ═══════════════════════════════════════════════════════════════════════════

export const ERROR_CODES = {
  STAMP_NOT_FOUND: 'BORDEREAU_001',
  INVALID_STAMP_FORMAT: 'BORDEREAU_002',
  DOCUMENT_NOT_ACCESSIBLE: 'BORDEREAU_003',
  EXTRACTION_FAILED: 'BORDEREAU_004',
  GENERATION_FAILED: 'BORDEREAU_005',
  NO_PIECES_NUMBERED: 'BORDEREAU_006',
  BORDEREAU_NOT_FOUND: 'BORDEREAU_007',
  INVALID_PIECE_ORDER: 'BORDEREAU_008',
  TOO_MANY_PIECES: 'BORDEREAU_009',
  STAMP_TOO_LARGE: 'BORDEREAU_010',
  DUPLICATE_PIECE_NUMBERS: 'BORDEREAU_011',
  EMPTY_PIECE_NUMBERS: 'BORDEREAU_012',
  QUEUE_ENQUEUE_FAILED: 'BORDEREAU_QUEUE_ENQUEUE_FAILED',
  GENERATION_COORDINATION_FAILED: 'BORDEREAU_GENERATION_COORDINATION_FAILED',
  GENERATION_SATURATED: 'BORDEREAU_GENERATION_SATURATED',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// USER-FRIENDLY ERROR MESSAGES
// ═══════════════════════════════════════════════════════════════════════════

export const ERROR_MESSAGES: Record<string, string> = {
  [ERROR_CODES.STAMP_NOT_FOUND]: "Le tampon sélectionné n'existe pas ou a été supprimé",
  [ERROR_CODES.INVALID_STAMP_FORMAT]: "Format de tampon non supporté. Utilisez SVG, PNG ou JPG",
  [ERROR_CODES.DOCUMENT_NOT_ACCESSIBLE]: "Un ou plusieurs documents ne sont pas accessibles",
  [ERROR_CODES.EXTRACTION_FAILED]: "L'extraction IA a échoué. Réessayez ou passez en mode manuel",
  [ERROR_CODES.GENERATION_FAILED]: "La génération du PDF a échoué. Veuillez réessayer",
  [ERROR_CODES.NO_PIECES_NUMBERED]: "Veuillez numéroter au moins une pièce avant de continuer",
  [ERROR_CODES.BORDEREAU_NOT_FOUND]: "Cette liste n'existe plus",
  [ERROR_CODES.INVALID_PIECE_ORDER]: "L'ordre des pièces est invalide",
  [ERROR_CODES.TOO_MANY_PIECES]: "Maximum 100 pièces par liste",
  [ERROR_CODES.STAMP_TOO_LARGE]: "Le tampon est trop volumineux (max 2MB)",
  [ERROR_CODES.DUPLICATE_PIECE_NUMBERS]: "Numéros de pièces dupliqués détectés. Corrigez les numéros en doublon.",
  [ERROR_CODES.EMPTY_PIECE_NUMBERS]: "Certaines pièces n'ont pas de numéro. Renseignez tous les numéros avant de continuer.",
  [ERROR_CODES.QUEUE_ENQUEUE_FAILED]: "La génération n'a pas pu démarrer. Relancez-la depuis le bordereau.",
  [ERROR_CODES.GENERATION_COORDINATION_FAILED]: "La génération est temporairement indisponible. Réessayez dans quelques instants.",
  [ERROR_CODES.GENERATION_SATURATED]: "Le service de génération est saturé. Relancez la génération dans quelques minutes.",

  // Frontend-specific errors
  NETWORK_ERROR: "Erreur de connexion. Vérifiez votre connexion internet",
  DUPLICATE_NUMBERS: "Les numéros de pièces doivent être uniques",
  NO_STAMP: "Veuillez sélectionner ou télécharger un tampon",
  NO_NUMBERS: "Veuillez numéroter toutes les pièces",
  NO_DOCUMENTS: "Veuillez sélectionner au moins un document",
  INVALID_ACTE_TEXT: "Veuillez saisir ou coller le texte de l'acte",
};

/**
 * Returns a user-friendly error message for the given error code
 */
export function getErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] || 'Une erreur inattendue est survenue';
}

// ═══════════════════════════════════════════════════════════════════════════
// UI CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

export const STEP_LABELS = {
  1: 'Pièces',
  2: 'Tampon',
  3: 'Export',
} as const;

export const STEP_DESCRIPTIONS = {
  1: 'Sélectionnez et numérotez vos pièces',
  2: 'Configurez le tampon',
  3: 'Téléchargez votre liste des pièces tamponnées',
} as const;

export const TOTAL_STEPS = 3;

// ═══════════════════════════════════════════════════════════════════════════
// POLLING INTERVALS
// ═══════════════════════════════════════════════════════════════════════════

export const EXTRACTION_POLL_INTERVAL_MS = 2000;
export const GENERATION_POLL_INTERVAL_MS = 1500;

// ═══════════════════════════════════════════════════════════════════════════
// QUICK NUMBERING PATTERNS
// ═══════════════════════════════════════════════════════════════════════════

export const QUICK_NUMBERING_PATTERNS = {
  numeric: {
    label: '1, 2, 3...',
    generator: (index: number) => String(index + 1),
  },
  alpha: {
    label: 'A, B, C...',
    generator: (index: number) => String.fromCharCode(65 + index),
  },
  piece: {
    label: 'Pièce 1, Pièce 2...',
    generator: (index: number) => `Pièce ${index + 1}`,
  },
} as const;

export type QuickNumberingPattern = keyof typeof QUICK_NUMBERING_PATTERNS;

// ═══════════════════════════════════════════════════════════════════════════
// STAMP POSITIONING CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/** Scale limits as per backend specification */
export const STAMP_SCALE = {
  MIN: 0.25,
  MAX: 3.0,
  STEP: 0.05,
  DEFAULT: 1.0,
} as const;

/** Default stamp position in mm from top-left */
export const STAMP_DEFAULT_POSITION = {
  X_MM: 10,
  Y_MM: 10,
} as const;

/** Fallback dimensions when data is unavailable */
export const STAMP_FALLBACK = {
  WIDTH_MM: 30,
  HEIGHT_MM: 30,
  CANVAS_WIDTH_PX: 64,
  CANVAS_HEIGHT_PX: 48,
  POSITION_PERCENT: 5,
} as const;

/** A4 page dimensions as fallback */
export const A4_DIMENSIONS = {
  WIDTH_MM: 210,
  HEIGHT_MM: 297,
} as const;
